import { type VariantProps, cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "../cn";

const licensePlateVariants = cva(
  "inline-flex select-none items-stretch overflow-hidden rounded-[5px] font-sans font-bold uppercase text-[#15171c] shadow-sm ring-1 ring-black/25 dark:shadow-none dark:ring-white/20",
  {
    variants: {
      size: {
        // w-[6.5rem] matches the Ledger concept's plate column width and ensures
        // every plate renders at the same fixed size regardless of character widths.
        sm: "h-6 text-xs w-[6.5rem]",
        md: "h-8 text-sm",
        lg: "h-10 text-xl",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const euBandVariants = cva("flex items-stretch bg-[#003399]", {
  variants: {
    size: {
      sm: "w-3.5",
      md: "w-4",
      lg: "w-5",
    },
  },
  defaultVariants: { size: "md" },
});

interface LicensePlateProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof licensePlateVariants> {
  value: string;
}

/** A unit five-point star centred on the origin (outer radius 1). */
const STAR_PATH =
  "M0,-1 0.2245,-0.309 0.9511,-0.309 0.3633,0.118 0.5878,0.809 0,0.382 -0.5878,0.809 -0.3633,0.118 -0.9511,-0.309 -0.2245,-0.309Z";

/** Twelve stars evenly spaced around a circle, mirroring the EU flag. */
const EU_STARS = Array.from({ length: 12 }, (_, index) => {
  const angle = (index / 12) * Math.PI * 2 - Math.PI / 2;
  return {
    id: index,
    x: 10 + Math.cos(angle) * 6.2,
    y: 12.5 + Math.sin(angle) * 6.2,
  };
});

/**
 * The blue EU identification band: circle of stars above the "NL" country
 * code. Exported so {@link LicensePlateInput} can render the identical band
 * next to an editable field instead of duplicating the star geometry.
 */
export function EuBand({ size }: VariantProps<typeof euBandVariants>) {
  return (
    <span className={euBandVariants({ size })}>
      <svg
        viewBox="0 0 20 40"
        className="h-full w-full"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {EU_STARS.map((star) => (
          <path
            key={star.id}
            d={STAR_PATH}
            transform={`translate(${star.x} ${star.y}) scale(1.45)`}
            fill="#ffcc00"
          />
        ))}
        <text
          x="10"
          y="34"
          textAnchor="middle"
          fontSize="9.5"
          fontWeight="700"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fill="#ffffff"
        >
          NL
        </text>
      </svg>
    </span>
  );
}

/**
 * Dutch (NL) license plate. The yellow body and blue EU band are intentionally
 * fixed in both light and dark mode, mirroring a real plate; only the outer edge
 * adapts so the plate stays crisp on a dark canvas instead of floating.
 */
export function LicensePlate({
  value,
  size,
  className,
  ...props
}: LicensePlateProps) {
  return (
    <span
      role="img"
      aria-label={`Nederlands kenteken ${value}`}
      {...props}
      className={cn(licensePlateVariants({ size }), className)}
    >
      <EuBand size={size} />
      <span
        className="flex flex-1 items-center whitespace-nowrap bg-[#f4c404] px-2"
        aria-hidden="true"
      >
        {value}
      </span>
    </span>
  );
}
